// ============================================================
// TURN TRACKER - Pure state transition functions
// ============================================================
// All functions are pure (no side effects, no React dependency).
// They accept a TurnTrackerState and return a new state.
// ============================================================

import type { TurnTrackerState, TurnParticipant, TurnOrderMode, Target, ParticipantStatus } from '../types';
import {
  DEFAULT_INITIATIVE_TIE_BREAK,
  arrivalStatus,
  sortByInitiative,
  type InitiativeArrival,
  type InitiativeTieBreak,
} from './initiative';

// ── Result types ─────────────────────────────────────────────

export interface AdvanceTurnResult {
  state: TurnTrackerState;
  roundAdvanced: boolean;
  pausedBetweenTurns: boolean;
  newActiveParticipant: TurnParticipant | null;
  /**
   * Combatants whose turn this advance passed over because they are down.
   *
   * A skipped turn is still a turn going by, so the caller must tick their
   * timed conditions - otherwise a Dying(2) combatant is skipped for being
   * Dying, never counts down, and stays Dying for ever.
   */
  skipped: string[];
}

export interface AdvanceOptions {
  /**
   * Combatant ids whose turn is passed over - the dead and the
   * unconscious, derived from their conditions by the caller.
   *
   * Skipped, never marked done and never removed: reviving somebody
   * puts them straight back in the order at the initiative they
   * already had, with nothing to re-enter and nothing to reroll.
   */
  downed?: ReadonlySet<string>;
}

/**
 * THE SCAN. The next actor is the first PENDING participant in list
 * order - index 0 upwards, not `activeIndex + 1` upwards.
 *
 * In a list sorted by initiative that is the highest remaining number
 * by definition, so a mid-round arrival who sorts above the pointer is
 * picked up on the very next advance rather than waiting out the round
 * behind slower combatants. Status is per-round and only `advanceRound`
 * resets it, so scanning from the top cannot give anyone a second turn.
 *
 * Returns -1 when nobody is eligible, which is what turns the round.
 */
function nextActor(ps: readonly TurnParticipant[], downed?: ReadonlySet<string>, skipped?: string[]): number {
  for (let i = 0; i < ps.length; i++) {
    if (ps[i].status !== 'pending') continue;
    const id = ps[i].combatantId;
    if (!id || !downed?.has(id)) return i;
    // WHOSE TURN THIS PASSED OVER. A skipped turn is still a turn going
    // by, and the caller has to tick their conditions or a timed one can
    // never expire - a combatant Dying(2) was skipped for being Dying,
    // so it never counted down and they were Dying for ever. Reported
    // from a real session (2026-08-19).
    skipped?.push(id);
  }
  return -1;
}

// ── State transitions ────────────────────────────────────────

/** The state without its surprise block - a round ended, the order reset, or the tracker switched off. */
function withoutSurprise(state: TurnTrackerState): TurnTrackerState {
  if (!state.surprise) return state;
  const { surprise: _over, ...rest } = state;
  return rest;
}

/**
 * Who sits out THIS round, by combatant id: the surprised, while the round
 * they were surprised in lasts.
 */
export function sittingOut(state: Pick<TurnTrackerState, 'round' | 'surprise'>): ReadonlySet<string> {
  return new Set(state.surprise?.round === state.round ? state.surprise.sitsOut : []);
}

/**
 * SPRING A SURPRISE on these combatants for the current round. If the one
 * acting is among them, the turn passes to the first who is not - they
 * stay pending, so the advance walks over them like anyone whose turn is
 * passed, and their conditions tick. With nobody to surprise this is the
 * state it was given.
 */
export function withSurprise(state: TurnTrackerState, sitsOut: readonly string[]): TurnTrackerState {
  const out = new Set(sitsOut);
  if (out.size === 0) return withoutSurprise(state);
  const surprised = { ...state, surprise: { round: state.round, sitsOut: [...out] } };
  const acting = state.participants[currentIndex(state)];
  if (!acting?.combatantId || !out.has(acting.combatantId)) return surprised;
  const participants = state.participants.map((p) => (p === acting ? { ...p, status: 'pending' as const } : p));
  const next = participants.findIndex((p) => p.status === 'pending' && !(p.combatantId && out.has(p.combatantId)));
  // Everybody was surprised: nobody acts this round, and the pointer stays put.
  if (next < 0) return surprised;
  return { ...surprised, participants: activate(participants, next), activeIndex: next };
}

/** Reset all participants to pending, increment round, clear countdownTicked. */
export function advanceRound(state: TurnTrackerState): TurnTrackerState {
  return {
    // A surprise lasts the round it was sprung in.
    ...withoutSurprise(state),
    round: state.round + 1,
    // The join hint belongs to the round it happened in. A new round is
    // the natural expiry - the offer to reroll the others has gone stale.
    joined: [],
    participants: state.participants.map((p) => ({
      ...p,
      status: 'pending' as const,
      countdownTicked: false,
      // A new round returns the whole turn (v8.309.0).
      actionPoints: 0,
    })),
  };
}

/** Reset to round 1, all participants pending, first becomes active. */
export function resetRound(state: TurnTrackerState): TurnTrackerState {
  const participants = state.participants.map((p, i) => ({
    ...p,
    status: (i === 0 ? 'active' : 'pending') as 'active' | 'pending',
    countdownTicked: false,
    actionPoints: 0,
  }));
  return { ...withoutSurprise(state), round: 1, activeIndex: 0, participants };
}

/** Make participant `idx` the active one - a new list, the rest untouched. */
function activate(ps: readonly TurnParticipant[], idx: number): TurnParticipant[] {
  return ps.map((p, i) => (i === idx ? { ...p, status: 'active' as const } : p));
}

/** A turn that went nowhere: nobody to hand it to, or paused between turns. */
function stalledTurn(state: TurnTrackerState, pausedBetweenTurns: boolean): AdvanceTurnResult {
  return { state, roundAdvanced: false, pausedBetweenTurns, newActiveParticipant: null, skipped: [] };
}

/**
 * Nobody eligible - turn the round, and open it on the first living
 * participant. `Math.max(0, ...)` is the everyone-is-down case: somebody
 * has to hold the pointer, or the tracker has no active participant at
 * all and the bar reads as broken rather than as a wiped-out table.
 */
function turnTheRound(
  state: TurnTrackerState,
  ps: TurnParticipant[],
  downed: ReadonlySet<string> | undefined,
  skipped: string[],
): AdvanceTurnResult {
  const nextState = advanceRound({ ...state, participants: ps });
  const firstIdx = Math.max(0, nextActor(nextState.participants, downed, skipped));
  nextState.activeIndex = firstIdx;
  nextState.participants = activate(nextState.participants, firstIdx);
  return {
    state: nextState,
    roundAdvanced: true,
    pausedBetweenTurns: false,
    newActiveParticipant: nextState.participants[firstIdx] ?? null,
    skipped,
  };
}

/**
 * Whose turn is being ended: the participant who IS acting, and only when
 * nobody is (paused between turns) the pointer. A pointer that drifted
 * from the status (a document merged before v8.399.8) used to end a
 * finished turn again and leave the acting one standing - two actives.
 */
function currentIndex(state: TurnTrackerState): number {
  const acting = state.participants.findIndex((p) => p.status === 'active');
  return acting >= 0 ? acting : state.activeIndex;
}

/** Advance to the next participant. Nobody left pending → new round. */
export function advanceTurn(state: TurnTrackerState, opts: AdvanceOptions = {}): AdvanceTurnResult {
  if (state.participants.length === 0) return stalledTurn(state, false);
  // Mark current as done
  const current = currentIndex(state);
  const ps = state.participants.map((p, i) => (i === current ? { ...p, status: 'done' as const } : { ...p }));
  if (state.paused) return stalledTurn({ ...state, participants: ps }, true);

  const skipped: string[] = [];
  // The surprised are passed over this round exactly as the downed are.
  const passedOver = new Set([...(opts.downed ?? []), ...sittingOut(state)]);
  const nextIdx = nextActor(ps, passedOver, skipped);
  if (nextIdx < 0) return turnTheRound(state, ps, opts.downed, skipped);
  ps[nextIdx] = { ...ps[nextIdx], status: 'active' };
  return {
    state: { ...state, participants: ps, activeIndex: nextIdx },
    roundAdvanced: false,
    pausedBetweenTurns: false,
    newActiveParticipant: ps[nextIdx],
    skipped,
  };
}

/** Add a participant. shouldActThisRound=false → done+ticked. */
export function addParticipant(
  state: TurnTrackerState,
  participant: TurnParticipant,
  shouldActThisRound: boolean,
): TurnTrackerState {
  // Prevent duplicate tab-linked participants
  if (participant.combatantId && state.participants.some((p) => p.combatantId === participant.combatantId)) {
    return state;
  }

  const isEmpty = state.participants.length === 0;

  const newP: TurnParticipant = {
    ...participant,
    status: isEmpty ? 'active' : shouldActThisRound ? 'pending' : 'done',
    countdownTicked: shouldActThisRound ? false : true,
  };

  return {
    ...state,
    participants: [...state.participants, newP],
    activeIndex: isEmpty ? 0 : state.activeIndex,
  };
}

/**
 * The active participant has left: hand the turn on. The same scan
 * `advanceTurn` uses. This used to search forward from the removal point
 * and then wrap - a second, subtly different rule for "who is next"
 * living in the same file. One rule now. All remaining done - advance
 * the round and open it on the first.
 */
function handOnAfterRemoval(state: TurnTrackerState, ps: TurnParticipant[]): TurnTrackerState {
  const nextIdx = nextActor(ps);
  if (nextIdx >= 0) return { ...state, participants: activate(ps, nextIdx), activeIndex: nextIdx };
  const newState = advanceRound({ ...state, participants: ps });
  newState.activeIndex = 0;
  newState.participants = activate(newState.participants, 0);
  return newState;
}

/** Remove a participant. Adjusts activeIndex and handles edge cases. */
export function removeParticipant(state: TurnTrackerState, participantId: string): TurnTrackerState {
  const idx = state.participants.findIndex((p) => p.id === participantId);
  if (idx < 0) return state;
  const ps = state.participants.filter((_, i) => i !== idx);
  if (ps.length === 0) return { ...state, participants: [], activeIndex: 0 };
  if (idx === state.activeIndex) return handOnAfterRemoval(state, ps);
  // Not active - the pointer follows the active participant down one if the removal was above it.
  const newActiveIdx = idx < state.activeIndex ? state.activeIndex - 1 : state.activeIndex;
  return { ...state, participants: ps, activeIndex: newActiveIdx };
}

/**
 * A stand-in takes the departed's slot (v8.342.0): same index, same
 * initiative and its parts, same status, same tick and action points -
 * only the identity changes. That is what "replace" means at the table:
 * the thing that was acting third is still acting third, it has merely
 * been revealed as something else. A newcomer who should ROLL for a
 * place is an arrival, and goes through `addParticipants`.
 *
 * The same state comes back when the departed is not tracked, or when
 * the newcomer already is - one row per person, never two.
 */
export function replaceParticipant(
  state: TurnTrackerState,
  outgoingCombatantId: string,
  incomingCombatantId: string,
  incomingName: string,
): TurnTrackerState {
  const idx = state.participants.findIndex((p) => p.combatantId === outgoingCombatantId);
  if (idx < 0) return state;
  if (state.participants.some((p) => p.combatantId === incomingCombatantId)) return state;
  const participants = state.participants.map((p, i) =>
    i === idx ? { ...p, id: `tab-${incomingCombatantId}`, combatantId: incomingCombatantId, name: incomingName } : p,
  );
  return { ...state, participants };
}

/** Reorder participants via drag. Adjusts activeIndex to follow the active participant. */
export function reorderParticipants(state: TurnTrackerState, fromIndex: number, toIndex: number): TurnTrackerState {
  if (fromIndex === toIndex) return state;
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= state.participants.length || toIndex >= state.participants.length) {
    return state;
  }

  const ps = [...state.participants];
  const [moved] = ps.splice(fromIndex, 1);
  ps.splice(toIndex, 0, moved);

  // Track where the active participant ended up
  let newActiveIdx = state.activeIndex;
  if (fromIndex === state.activeIndex) {
    newActiveIdx = toIndex;
  } else if (fromIndex < state.activeIndex && toIndex >= state.activeIndex) {
    newActiveIdx = state.activeIndex - 1;
  } else if (fromIndex > state.activeIndex && toIndex <= state.activeIndex) {
    newActiveIdx = state.activeIndex + 1;
  }

  return { ...state, participants: ps, activeIndex: newActiveIdx };
}

// ── Initiative and the order it produces ─────────────────────

/**
 * Put the list in initiative order, keeping the SAME participant acting.
 *
 * `activeIndex` is positional, so a sort would otherwise silently hand
 * the turn to whoever landed on the old index. Re-finding the active
 * participant by id is the whole of the bookkeeping a re-sort needs -
 * status is per-round rather than positional, so a chip moving above
 * the pointer cannot act twice and one moving below cannot be skipped.
 *
 * Sorts regardless of `orderMode`: this is the primitive, and it is
 * what "back to initiative" calls from a manually-ordered combat.
 */
export function sortParticipants(
  state: TurnTrackerState,
  tieBreak: InitiativeTieBreak = DEFAULT_INITIATIVE_TIE_BREAK,
): TurnTrackerState {
  if (state.participants.length === 0) return state;

  const activeId = state.participants[state.activeIndex]?.id ?? null;
  const participants = sortByInitiative(state.participants, tieBreak);
  const found = activeId ? participants.findIndex((p) => p.id === activeId) : -1;

  return { ...state, participants, activeIndex: found >= 0 ? found : 0 };
}

/** Re-sort only when the order is supposed to derive from initiative. */
function autoSort(state: TurnTrackerState, tieBreak: InitiativeTieBreak): TurnTrackerState {
  return state.orderMode === 'initiative' ? sortParticipants(state, tieBreak) : state;
}

/** One participant's rolled result, as handed back by `rollInitiative`. */
export interface InitiativeAssignment {
  participantId: string;
  roll: number;
  bonus: number;
}

/**
 * Write rolled initiatives in, and re-sort if the order derives from them.
 *
 * Dice-free by design - the caller rolls and hands the numbers over,
 * exactly as `turnEffects.ts` does. That is what keeps this module pure
 * and lets the whole ordering story be tested on literals.
 *
 * A rolled value always clears `initiativeManual`: it is no longer a
 * human's ruling once the dice have replaced it.
 */
export function applyInitiative(
  state: TurnTrackerState,
  assignments: readonly InitiativeAssignment[],
  tieBreak: InitiativeTieBreak = DEFAULT_INITIATIVE_TIE_BREAK,
): TurnTrackerState {
  if (assignments.length === 0) return state;

  const byId = new Map(assignments.map((a) => [a.participantId, a]));
  const participants = state.participants.map((p) => {
    const a = byId.get(p.id);
    if (!a) return p;
    return {
      ...p,
      initiative: a.roll + a.bonus,
      initiativeRoll: a.roll,
      initiativeBonus: a.bonus,
      initiativeManual: false,
    };
  });

  return autoSort({ ...state, participants }, tieBreak);
}

/**
 * Set a participant's initiative by hand.
 *
 * Marks it manual, which is what a bulk reroll consults before
 * overwriting it. Blanking the box clears the whole record - leaving a
 * stale roll and bonus behind would make the breakdown tooltip lie.
 */
export function setInitiative(
  state: TurnTrackerState,
  participantId: string,
  initiative: number | null,
  tieBreak: InitiativeTieBreak = DEFAULT_INITIATIVE_TIE_BREAK,
): TurnTrackerState {
  const idx = state.participants.findIndex((p) => p.id === participantId);
  if (idx < 0) return state;

  const ps = state.participants.map((p, i) =>
    i === idx
      ? {
          ...p,
          initiative,
          initiativeManual: initiative !== null,
          ...(initiative === null ? { initiativeRoll: null, initiativeBonus: null } : {}),
        }
      : p,
  );
  return autoSort({ ...state, participants: ps }, tieBreak);
}

/** Switch between a derived order and a hand-made one. Going back re-sorts. */
export function setOrderMode(
  state: TurnTrackerState,
  mode: TurnOrderMode,
  tieBreak: InitiativeTieBreak = DEFAULT_INITIATIVE_TIE_BREAK,
): TurnTrackerState {
  if (state.orderMode === mode) return state;
  const next = { ...state, orderMode: mode };
  return mode === 'initiative' ? sortParticipants(next, tieBreak) : next;
}

/** Clear the "N joined" hint without rerolling anything. */
export function dismissJoinHint(state: TurnTrackerState): TurnTrackerState {
  return state.joined.length === 0 ? state : { ...state, joined: [] };
}

export interface AdmitOptions {
  arrival: InitiativeArrival;
  tieBreak?: InitiativeTieBreak;
}

/**
 * THE DOOR into the turn order. Every admission route goes through here.
 *
 * Participants arrive with their initiative already rolled (the caller
 * owns the dice and the sheets). This decides only what the arrival
 * ruling decides - a status - then inserts, sorts and records who is
 * new so the bar can offer to reroll everyone else.
 *
 * `activeTotal` is read ONCE from the incoming state, so a batch of
 * arrivals is judged against whoever is acting rather than against each
 * other.
 */
export function addParticipants(
  state: TurnTrackerState,
  incoming: readonly TurnParticipant[],
  opts: AdmitOptions,
): TurnTrackerState {
  if (incoming.length === 0) return state;

  const tieBreak = opts.tieBreak ?? DEFAULT_INITIATIVE_TIE_BREAK;
  const activeTotal = state.participants[state.activeIndex]?.initiative ?? null;
  // An empty tracker is a combat being set up, not a combat being joined.
  const wasEmpty = state.participants.length === 0;

  let next = state;
  const added: string[] = [];
  for (const p of incoming) {
    const total = p.initiative ?? Number.NEGATIVE_INFINITY;
    const shouldAct = arrivalStatus(opts.arrival, total, activeTotal) === 'pending';
    const before = next.participants.length;
    next = addParticipant(next, p, shouldAct);
    if (next.participants.length > before) added.push(p.id); // a duplicate was refused
  }

  if (added.length === 0) return state;

  return autoSort({ ...next, joined: wasEmpty ? next.joined : [...next.joined, ...added] }, tieBreak);
}

/** Deactivate tracker. keepState preserves order/round; false clears everything. */
export function deactivateTracker(state: TurnTrackerState, keepState: boolean): TurnTrackerState {
  if (!keepState) {
    return {
      active: false,
      round: 1,
      activeIndex: 0,
      participants: [],
      paused: false,
      orderMode: 'initiative',
      joined: [],
    };
  }
  return { ...withoutSurprise(state), active: false, activeIndex: 0 };
}

/** Mark countdown as ticked for a participant by combatantId. */
export function markCountdownTicked(state: TurnTrackerState, combatantId: string): TurnTrackerState {
  const idx = state.participants.findIndex((p) => p.combatantId === combatantId);
  if (idx < 0) return state;

  const ps = state.participants.map((p, i) => (i === idx ? { ...p, countdownTicked: true } : p));
  return { ...state, participants: ps };
}

// ── Queries ──────────────────────────────────────────────────

/** Check if a given combatantId is the currently active turn participant. */
export function isActiveParticipant(state: TurnTrackerState, combatantId: string): boolean {
  const active = state.participants[state.activeIndex];
  return active?.combatantId === combatantId && active?.status === 'active';
}

/** The combatant whose turn it is while the order runs, or null (no order, or a turn with no combatant). */
export function actingCombatantId(state: TurnTrackerState | null | undefined): string | null {
  if (!state?.active) return null;
  return state.participants[currentIndex(state)]?.combatantId ?? null;
}

/** Check if a given combatantId is tracked in the turn tracker. */
export function isTrackedParticipant(state: TurnTrackerState, combatantId: string): boolean {
  return state.participants.some((p) => p.combatantId === combatantId);
}

/**
 * Determine whether per-attack countdown should run for a target.
 * Returns false if the target is a tracked participant (countdown fires at turn-start)
 * or if countdown already ticked this round.
 */
export function shouldRunPerAttackCountdown(
  target: Target,
  trackerState: TurnTrackerState | null,
  combatantId: string,
  enabled: boolean,
): boolean {
  if (!enabled) return false;
  if (target.countdownTickedThisRound) return false;
  if (!trackerState || !trackerState.active) return true;
  if (isTrackedParticipant(trackerState, combatantId)) return false;
  return true;
}

/**
 * Accept a turn-tracker state written by an older client.
 *
 * Participants used to carry `tabId`; v6.58.0 renamed the field to
 * `combatantId` to say what it actually points at. The document is
 * persisted, so a session mid-fight when the deploy lands would
 * otherwise come back with every participant unlinked - the tracker
 * would still show names but countdown ticks and "whose turn is it"
 * would silently stop resolving.
 *
 * v8.69.0 adds the initiative fields and `orderMode`. A document with
 * NO `orderMode` is normalised to **`'manual'`**, deliberately: it was
 * written by a client where the only way to order the bar was to drag
 * it, so that order is somebody's hand-made ruling. Defaulting it to
 * `'initiative'` would resort a live session's turn order under the
 * players the moment this deploys - and every participant's initiative
 * is null there, so it would resort it into arrival order at that. A
 * fresh tracker starts `'initiative'`; only inherited ones start manual.
 */
export function normalizeTurnTrackerState(raw: TurnTrackerState | null): TurnTrackerState | null {
  if (!raw?.participants) return raw;
  const participants = raw.participants.map(normalizeParticipant);
  const changed =
    participants.some((p, i) => p !== raw.participants[i]) || raw.orderMode === undefined || raw.joined === undefined;
  return changed ? { ...raw, participants, orderMode: raw.orderMode ?? 'manual', joined: raw.joined ?? [] } : raw;
}

/**
 * One participant from an older client: the legacy `tabId` link moved to
 * `combatantId`, and the initiative fields filled in. The same object
 * comes back when there is nothing to fix.
 *
 * actionPoints (v8.309.0) is deliberately NOT back-filled here. It is
 * optional and every reader goes through `economyOf`, which coerces
 * undefined to 0 - so there is no NaN to prevent, and back-filling would
 * rewrite every live session's tracker doc on load for a field the next
 * `advanceRound` sets anyway.
 */
function normalizeParticipant(p: TurnParticipant): TurnParticipant {
  const legacy = (p as TurnParticipant & { tabId?: string | null }).tabId;
  const needsLink = p.combatantId == null && legacy != null;
  const needsFields = p.initiativeRoll === undefined || p.initiativeManual === undefined;
  if (!needsLink && !needsFields) return p;
  return {
    ...p,
    combatantId: needsLink ? legacy! : p.combatantId,
    initiative: p.initiative ?? null,
    initiativeRoll: p.initiativeRoll ?? null,
    initiativeBonus: p.initiativeBonus ?? null,
    initiativeManual: p.initiativeManual ?? false,
  };
}

// ============================================================
// Convergence - one document, many writers (v8.310.6)
// ============================================================

/**
 * Apply an edit made against `base` onto `server`, the document as it
 * stands now.
 *
 * The tracker is ONE JSON document that every client rewrites whole - a
 * plate press charges action points, the GM removes a chip, a player ends
 * their turn - and last-write-wins on a whole array is how a removed
 * participant walked back in: a peer still holding the old array wrote it
 * back. This is the three-way merge that lets every writer keep writing
 * from its own copy while the server converges:
 *
 * - Participants are matched by id. One the edit REMOVED is removed; one it
 *   ADDED is added; one both sides know takes the edit's value for each
 *   field the edit changed and the server's for the rest, so a plate press
 *   and a removal on different chips never collide.
 * - The ORDER is the edit's when the edit changed it (a reorder, an
 *   arrival, a removal), with any participant only the server knows
 *   appended - otherwise the server's.
 * - Every top-level scalar the edit changed is the edit's; the rest are the
 *   server's. `activeIndex` is clamped afterwards, since a removal by one
 *   side and an advance by the other can leave it past the end.
 *
 * Deterministic and free of dice: an edit that rolled initiative carries
 * the rolled values as data, so replaying it on a fresh server state
 * re-rolls nothing.
 */
export function mergeTrackerChange(
  server: TurnTrackerState | null,
  base: TurnTrackerState,
  next: TurnTrackerState,
): TurnTrackerState {
  if (!server) return next;
  if (JSON.stringify(server) === JSON.stringify(base)) return next;

  const sides: MergeSides = {
    base: new Map(base.participants.map((p) => [p.id, p])),
    next: new Map(next.participants.map((p) => [p.id, p])),
    server: new Map(server.participants.map((p) => [p.id, p])),
  };
  const participants = mergedOrder(server, base, next, sides)
    .map((id) => mergeParticipant(sides.server.get(id), sides.base.get(id), sides.next.get(id)))
    .filter((p): p is TurnParticipant => p !== null);

  const merged = { ...overlayChanges(server, base, next, 'participants'), participants };
  merged.activeIndex = participants.length ? Math.min(Math.max(0, merged.activeIndex), participants.length - 1) : 0;
  return oneTurn(merged, sides.server);
}

/**
 * ONE TURN AT A TIME (v8.398.5). Field by field, two moves of the turn
 * made from one state both survive: a screen that missed two End Turns
 * (x -> y -> z) ends x's turn again, its edit sets y active, the server
 * keeps z, and the table has two people taking their turn - a fuzz find,
 * on every screen at once. The server's move stands - it landed first,
 * and the stale one ended a turn that was already over - and anyone else
 * the edit made active returns to the server's status for them, or to
 * pending if the server has not heard of them. Nobody active on the
 * server: the edit's move stands. A document that already holds two
 * actives is healed the same way on its next merge.
 */
function oneTurn(merged: TurnTrackerState, server: ReadonlyMap<string, TurnParticipant>): TurnTrackerState {
  const active = merged.participants.filter((p) => p.status === 'active');
  // One acting: the pointer names them. The edit's pointer can name someone
  // else - a sort that met a move of the turn (overnight fuzz, v8.399.8).
  if (active.length === 1) return { ...merged, activeIndex: merged.participants.indexOf(active[0]) };
  if (active.length === 0) return merged;
  const keep = active.find((p) => server.get(p.id)?.status === 'active') ?? active[0];
  const participants = merged.participants.map((p) =>
    p.status !== 'active' || p === keep ? p : { ...p, status: statusBefore(server.get(p.id)) },
  );
  return { ...merged, participants, activeIndex: participants.indexOf(keep) };
}

/**
 * Where a demoted participant returns: its status on the server - never
 * 'active', which a document written before v8.398.5 may hold twice.
 */
function statusBefore(onServer: TurnParticipant | undefined): ParticipantStatus {
  return onServer && onServer.status !== 'active' ? onServer.status : 'pending';
}

/** The three copies of the participants, by id. */
interface MergeSides {
  base: Map<string, TurnParticipant>;
  next: Map<string, TurnParticipant>;
  server: Map<string, TurnParticipant>;
}

/**
 * `onto`, with every field the edit changed (base → next, compared as JSON)
 * taken from `next`. Fields keep `onto`'s order; new ones append.
 */
function overlayChanges<T extends object>(onto: T, base: T, next: T, skip?: string): T {
  const out = { ...onto } as Record<string, unknown>;
  const bb = base as Record<string, unknown>;
  const nn = next as Record<string, unknown>;
  for (const key of new Set([...Object.keys(bb), ...Object.keys(nn)])) {
    if (key !== skip && JSON.stringify(bb[key]) !== JSON.stringify(nn[key])) out[key] = nn[key];
  }
  return out as T;
}

/** One participant, merged from the three copies; null when it is gone. */
function mergeParticipant(
  s: TurnParticipant | undefined,
  b: TurnParticipant | undefined,
  n: TurnParticipant | undefined,
): TurnParticipant | null {
  if (!n) return s ?? null; // only the server knows it: keep it as is
  // Absent from the server but known to the base: the SERVER removed it,
  // and the edit merely still remembers it. Not a resurrection.
  if (!s) return b ? null : n;
  if (!b) return n; // both added it: the edit's copy
  return overlayChanges(s, b, n);
}

const idList = (ps: readonly TurnParticipant[]): string => ps.map((p) => p.id).join('|');

/**
 * The merged order of ids: the edit's when the edit changed it, else the
 * server's - less what the edit removed, plus what the other side ADDED.
 * A chip the base knew and the source lacks was removed by the source,
 * not added by the other.
 */
function mergedOrder(
  server: TurnTrackerState,
  base: TurnTrackerState,
  next: TurnTrackerState,
  sides: MergeSides,
): string[] {
  const removed = new Set([...sides.base.keys()].filter((id) => !sides.next.has(id)));
  const orderChanged = idList(base.participants) !== idList(next.participants);
  const [source, other] = orderChanged ? [next, server] : [server, next];
  const ids = source.participants.map((p) => p.id).filter((id) => !removed.has(id));
  const known = new Set(ids);
  for (const p of other.participants) {
    if (known.has(p.id) || removed.has(p.id) || sides.base.has(p.id)) continue;
    ids.push(p.id);
    known.add(p.id);
  }
  return ids;
}

/**
 * Add spent action points to one tracked combatant (2026-10-26: a turn-start Full Action, Pinned's shake-off),
 * or the state unchanged when the tracker is off or he is not in the order. Pure; never below zero.
 */
export function chargedActionPoints(state: TurnTrackerState, combatantId: string, points: number): TurnTrackerState {
  if (!state?.active || !points) return state;
  const idx = state.participants.findIndex((p) => p.combatantId === combatantId);
  if (idx < 0) return state;
  const value = Math.max(0, (state.participants[idx].actionPoints ?? 0) + points);
  return { ...state, participants: state.participants.map((p, i) => (i === idx ? { ...p, actionPoints: value } : p)) };
}
